import { EditType } from 'src/types/edit';
import { InsightSummaryPage } from 'src/types/pages/insights';

export const PROCESS: InsightSummaryPage = {
  page_id: 'insights-42',
  section_order: ['header', 'summary'],
  header: {
    visible: true,
    content: {
      title: 'Process',
      body: 'Mediators share their thoughts, feelings, and insights on the experience of participating in these small group conversations.',
      header_type: 'full',
    },
  },
  summary: {
    visible: true,
    content: {
      highlight_link_style: {
        default_color: '#11304B',
        active_color: '#48bf53',
      },
      paragraphs: [
        {
          label: 'Overview & Recommendations',
          text: 'Many participants stated they enjoyed the experience of <Embed getEmbedInfo={getEmbedInfo}>being included-5249379</Embed> in this pilot program. They appreciated the opportunity to share their thoughts, hearts, and experiences with their colleagues. It left the participants feeling positive about the agency and closer to their fellow FMCS members. There is a vision that this <Embed getEmbedInfo={getEmbedInfo}>process-5250377</Embed> could be used to support the agency’s cultural and <Embed getEmbedInfo={getEmbedInfo}>operational evolution-5250380</Embed> and a tool to help provide new and elevated services to external clients. A few people had trepidations about AI replacing people in casework, or express <Embed getEmbedInfo={getEmbedInfo}>abiding concerns-5250463</Embed> about how the agency will adjust to upcoming challenges, but all participants are keen to see the output of this initiative.<br/><br/>Recommendations from conversations include:<br/><ul><li>Because of the new operational philosophy and recent changes in the FMCS management structure, it is important for agency leadership to continually illustrate how these adjustments will be positive for FMCS members as individual contributors and members of work teams,  through town hall, departmental, regional and individual conversations</li><li>It is also crucial to show how these changes will support the perpetuation and relevance of the agency. Recommend ongoing processes like this pilot program continue. These are vital and healthy outlets for building individual confidence, cultivating relationships within the organization, and fostering an optimistic and inspiring esprit de corps within the agency.</li><li>Recommend additional sessions of facilitated conversations to encompass the entire agency over the next FY to realize and build upon the positives expressed by the feedback of the participants<ul><li>Optimism about the future of FMCS</li><li>Enjoyable and appreciated experience</li><li>Needed experience</li><li>Safe and encouraging experience</li><li>Rich experience</li></ul></li><li>Recommend developing pilot programs to test field applications of this platform in other areas of external casework and well as with internal initiatives like DEIA</li></ul>',
        },
        {
          label: 'Critique and Feedback',
          text: "Mediators had an open mind and expressed their readiness to participate as they were intrigued by the premise of sharing their thoughts among trusted colleagues. <br/><br/>However, <Embed getEmbedInfo={getEmbedInfo}>Speaker TB-5249191</Embed> shared that there was some hesitation among participants at the beginning; “I didn't quite know exactly what this was, getting into it. I don't know if I missed something that everybody else ... Or if everybody, “What is this, really?” In particular, the process of the conversations being recorded was a topic of discussion as participants weighed the benefits as well as the potential consequences. <Embed getEmbedInfo={getEmbedInfo}>Speaker BE-5249122</Embed> levels with their colleagues on how they view the process: “Look, I know this is being taped, but if it's only internally that's going to hear this, I've been here 30 years, all right? When people tell me they're worked to the bone, et cetera, et cetera, get a life, all right? I mean, and I am not one that keeps my mouth closed, and I'm very vocal and everybody knows where I stand on any issue.”<br/><br/>As the conversation concludes, <Embed getEmbedInfo={getEmbedInfo}>Speaker TB-5249191</Embed> shared their feeling it was a positive experience and in the future; “I'd be interested to see if what we all identified as the challenges is what rises to the top when the artificial intelligence gets a chance to parse through this audiotape and then really what comes of it. I'm assuming this is referred to as a pilot because this is just the start with us, but then to expand it out to the rest of the agency so that we get a full view of how everyone feels.”<br/><br/>In their reflection of the process, <Embed getEmbedInfo={getEmbedInfo}>Speaker DA-5249085</Embed> frames their experience with an eye toward the future and offers feedback on how FMCS may fulfill the needs of  all citizens for civil, effective, and productive conflict management beyond the service of clients. “My hope and dream expands beyond the agency. I'd like for FMCS to be seen as a major resource for helping resolve conflict in our society. We are so polarized right now in many ways, on an interpersonal level but of course in our politics.” Specifically, Speaker DA hopes that; “ we can become even more interdisciplinary. We're a little bit interdisciplinary now. I know we have interest in neuroscience and things like that, but it would be nice to maybe even have some people with social work and psychology backgrounds in our agency, without forgetting our mission to help labor and management.”<br/><br/>For mediators like <Embed getEmbedInfo={getEmbedInfo}>Speaker LA-5249047</Embed> who say the experience was “awesome,” they also saw many possibilities for LVN: “I can see so many ways that we can utilize this. I'm excited to see what happens on the other end, what the output looks like. I really like this as a way of really allowing a comfort zone of sharing.” They point out that in order to support the process; “You really need to be dialed in for you to see how you can share someone else's perspective or what part of that they shared can you relate with. So it really made you come outside of yourself and to see, am I like someone else's perspective or am I different or am I undecided?”",
        },
        {
          label: 'Approach-Strategy and Appreciation',
          text: "<Embed getEmbedInfo={getEmbedInfo}>Speaker WA-5249097</Embed> in their appraisal of the conversation facilitators, who were charged with the unique task of facilitating conversations among professional mediators, found that; “As far as the platform and the flow of conversation, to me it was pretty natural, and I think that was probably in my mind, as I evaluate it, it was platform driven, certainly facilitator driven. But then also participant driven, because I think we have a group here that my perception is people were comfortable talking about things.”<br/><br/><Embed getEmbedInfo={getEmbedInfo}>Speaker DA-5249098</Embed> expressed a combination of their appraisal of the platform and process as well as showing appreciation. Sometimes in leadership we talk to each other too much. “We need to get out there and hear people's ideas and connect with people. So that's an important takeaway. As far as the platform is concerned, to me, I didn't really think of it as a platform. I thought of it as a facilitated discussion. It was well facilitated, so that's good.” Speaker DA raises a particularly pointed question regarding process: “I'm curious though, if this were more cacophonous, if there was a real disagreement that we had and we were talking over each other and being real passionate, I'd like to see how this MIT thing would handle that, whether it's capable, right?” <br/><br/>In fact, several mediators including <Embed getEmbedInfo={getEmbedInfo}>Speaker DB-5249099</Embed>, <Embed getEmbedInfo={getEmbedInfo}>Speaker SA-5250380</Embed>, and <Embed getEmbedInfo={getEmbedInfo}>Speaker TC-5249488</Embed> praised the professionalism, effectiveness, and most of all, warmth exuding from the facilitators. Speaker DB pointed out that open communication was important to them and thought it was “a very organic experience.” And Speaker SA noted that they were; “extremely comfortable with the pace, the flow” and Speaker TC was especially grateful for the candor and confidence of their colleagues to be open to new and experimental techniques in the pursuit of innovating their field. Quite simply, “this was an opportunity to do that. I felt very just grateful to be a part of it.”",
        },
      ],
    },
  },
  edit_metadata: {
    page_label: 'Process',
    page_id: 'insights-42',
    subroute: true,
    sections: [
      {
        section_label: 'Introduction',
        edit_blocks: [
          {
            block_label: 'Header',
            match: 'header.content',
            type: EditType.banner,
          },
        ],
      },
      {
        section_label: 'Highlight Link Colors',
        edit_blocks: [
          {
            block_label: 'Color',
            match: 'summary.content.highlight_link_style.default_color',
            type: EditType.color,
          },
          {
            block_label: 'Active Color',
            match: 'summary.content.highlight_link_style.active_color',
            type: EditType.color,
          },
        ],
      },
      {
        section_label: 'Paragraphs',
        update_section: {
          match: 'summary.content.paragraphs',
          label: 'Add Paragraph',
          empty: {
            label: 'Default Label',
            text: 'Default Text',
          },
          newSectionMetadata: {
            section_label: 'Insight',
            numbered: true,
            edit_blocks: [
              {
                block_label: 'Title',
                match: `summary.content.paragraphs[index].label`,
                type: EditType.string,
              },
              {
                block_label: 'Paragraph',
                match: `summary.content.paragraphs[index].text`,
                type: EditType.embed_string,
              },
            ],
          },
        },
        sub_sections: [
          {
            section_label: 'Overview',
            edit_blocks: [
              {
                block_label: 'Title',
                match: 'summary.content.paragraphs[0].label',
                type: EditType.string,
              },
              {
                block_label: 'Paragraph',
                match: 'summary.content.paragraphs[0].text',
                type: EditType.embed_string,
              },
            ],
          },
          {
            section_label: 'Insight',
            numbered: true,
            edit_blocks: [
              {
                block_label: 'Title',
                match: 'summary.content.paragraphs[1].label',
                type: EditType.string,
              },
              {
                block_label: 'Paragraph',
                match: 'summary.content.paragraphs[1].text',
                type: EditType.embed_string,
              },
            ],
          },
          {
            section_label: 'Insight',
            numbered: true,
            edit_blocks: [
              {
                block_label: 'Title',
                match: 'summary.content.paragraphs[2].label',
                type: EditType.string,
              },
              {
                block_label: 'Paragraph',
                match: 'summary.content.paragraphs[2].text',
                type: EditType.embed_string,
              },
            ],
          },
        ],
      },
    ],
  },
};
